import React from 'react';

import { SharedModels } from '@project44-models/iam-gateway';

import { ReactComponent } from '../../../../../shared/abstract/infrastructrue/ReactComponent';
import { UnifiedRolesEditorPolicy } from '../../../../UnifiedRoles/domain/abstract';
import { TUnifiedRolesMap } from '../../../../UnifiedRoles/domain/interfaces';
import {
  IUserTenantSpecificRolesCheckboxSelectAllCreator,
  TUserTenantSpecificRolesSelectAllCheckbox,
} from '../../../domain/interfaces';
import { UseTenantSpecificRolesSelectAllCheckbox } from './UserTenantSpecificRolesSelectAllCheckbox.view';

export class UserTenantSpecificRolesSelectAllCheckbox extends ReactComponent {
  constructor(
    private readonly unifiedRolesEditorPolicy: UnifiedRolesEditorPolicy,
  ) {
    super();
  }

  public readonly handleOnChange = (
    isSelected: boolean,
    assignedUserTenantSpecificRoles: TUnifiedRolesMap,
  ): TUnifiedRolesMap => {
    return isSelected
      ? this.unifiedRolesEditorPolicy.addAllNonTenantAdminRelatedRolesToAssignedRoles(
          assignedUserTenantSpecificRoles,
        )
      : this.unifiedRolesEditorPolicy.removeAllNonTenantAdminRelatedRolesFromAssignedRoles(
          assignedUserTenantSpecificRoles,
        );
  };

  public readonly isAtLeastOneRoleSelected = (
    assignedUserTenantSpecificRoles: TUnifiedRolesMap,
  ) => {
    return Array.from(assignedUserTenantSpecificRoles.values()).some(
      (value) => {
        return (
          !!value &&
          !this.unifiedRolesEditorPolicy.isRoleTenantAdminRelatedRole(value)
        );
      },
    );
  };

  public readonly isIndeterminate = (
    assignedUserTenantSpecificRoles: TUnifiedRolesMap,
    availableTenantSpecificRoles: TUnifiedRolesMap,
  ): boolean => {
    return (
      this.isAtLeastOneRoleSelected(assignedUserTenantSpecificRoles) &&
      !this.isAllSelected(
        assignedUserTenantSpecificRoles,
        availableTenantSpecificRoles,
      )
    );
  };

  public readonly isAllSelected = (
    assignedUserTenantSpecificRoles: TUnifiedRolesMap,
    availableTenantSpecificRoles: TUnifiedRolesMap,
  ) => {
    const tenantSpecificRoles = this.filterTenantAdminRelatedRoles(
      Array.from(availableTenantSpecificRoles.values()),
    );

    const assignedTenantSpecificRoles = this.filterTenantAdminRelatedRoles(
      Array.from(assignedUserTenantSpecificRoles.values()),
    );

    const rolesGreaterThanZeo =
      tenantSpecificRoles.length > 0 && assignedTenantSpecificRoles.length > 0;

    return (
      rolesGreaterThanZeo &&
      tenantSpecificRoles.length === assignedTenantSpecificRoles.length
    );
  };

  public static create({
    isApiRequestInProgress,
    userPrincipal,
    assignedUserTenantSpecificRolesState,
    uiUserTenantSpecificRoles,
    availableTenantAgnosticRoles,
  }: IUserTenantSpecificRolesCheckboxSelectAllCreator): React.ReactElement<TUserTenantSpecificRolesSelectAllCheckbox> {
    const {
      userTenantSpecificRolesEditor: {
        getAllAvailableUnifiedRoles,
        canUserPrincipalUpdateTenantSpecificUnifiedRoles,
      },
    } = uiUserTenantSpecificRoles;

    const isEditableByUserPrincipal =
      canUserPrincipalUpdateTenantSpecificUnifiedRoles(userPrincipal);

    const withLoadingStatus: React.FC<TUserTenantSpecificRolesSelectAllCheckbox> =
      this.composeWithLoadingStatus<TUserTenantSpecificRolesSelectAllCheckbox>(
        UseTenantSpecificRolesSelectAllCheckbox,
        {
          withLoadingStatusOptions: {
            isLoading: isApiRequestInProgress,
            css: {
              h: '21px',
              w: '21px',
            },
          },
        },
      );

    return this.withAnyOfPermissionsHOC<TUserTenantSpecificRolesSelectAllCheckbox>(
      [
        'SAVE_USER_ROLES',
        'system-admin:read:tenant-specific-user-role-assignments',
      ],
      withLoadingStatus,
      {
        availableTenantSpecificRoles: new Map(
          getAllAvailableUnifiedRoles()
            .toSerializedModel()
            .roles.map((role) => {
              return [role.id, role];
            }),
        ),
        config: new UserTenantSpecificRolesSelectAllCheckbox(
          uiUserTenantSpecificRoles.userTenantSpecificRolesEditor,
        ),
        user: userPrincipal,
        isApiRequestInProgress,
        availableTenantAgnosticRoles,
        assignedUserTenantSpecificRolesState,
        isEditableByUserPrincipal,
      },
    );
  }

  private readonly filterTenantAdminRelatedRoles = (
    roles: Array<SharedModels.UnifiedRoleViewModel | undefined>,
  ) => {
    return roles
      .filter((role) => {
        return this.unifiedRolesEditorPolicy.isRoleTenantAdminRelatedRole(role)
          ? undefined
          : role;
      })
      .filter(Boolean);
  };
}
